import Fuse from "fuse.js";
import SearchIcon from "@mui/icons-material/Search";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
} from "@mui/joy";
import { useTranslation } from "../../../../i18n";
import {
  InspectionLotInput,
  Sku,
  useSkuListQuery,
} from "../../../../__generated__/types-and-hooks";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SkuRef from "../../../../components/SkuRef";

const JoyCheckbox = React.forwardRef(function JoyCheckbox(props: any, ref) {
  const { indeterminate, checked, ...other } = props;
  return (
    <Checkbox
      ref={ref}
      indeterminate={indeterminate}
      checked={checked}
      variant="outlined"
      color="neutral"
      {...other}
    />
  );
});

interface SkusTableModalProps {
  team: { id: number; slug: string };
  open: boolean;
  onClose: () => void;
  onSave: (inspectionLots: InspectionLotInput[]) => void;
}

export default function SkusTableModal({
  team,
  onSave,
  open,
  onClose,
}: SkusTableModalProps) {
  const t = useTranslation();
  const [search, setSearch] = useState<string | null>(null);
  const [skusFiltered, setSkusFiltered] = useState<Sku[] | undefined>(
    undefined
  );

  const RefRenderCell = useCallback(
    (params: GridRenderCellParams<any, string>) => {
      return <SkuRef uniqueId={params.row.uniqueId} teamSlug={team.slug} />;
    },
    [team]
  );
  const skuListQuery = useSkuListQuery({
    variables: { teamId: team.id },
  });

  const skus = skuListQuery.data && skuListQuery.data.sku_list?.skus;

  const fuse = useMemo(() => {
    if (!skus) return null;

    return new Fuse(skus, {
      keys: [
        { name: "uniqueId", weight: 0.8 },
        { name: "sku", weight: 0.5 },
        { name: "name", weight: 0.3 },
        { name: "description", weight: 0.2 },
        { name: "category", weight: 0.1 },
        { name: "material", weight: 0.1 },
        { name: "asin", weight: 0.1 },
        { name: "hs", weight: 0.1 },
        { name: "hts", weight: 0.1 },
        { name: "upc", weight: 0.1 },
      ],
      threshold: 0.2, // 0 is exact, 1 is very fuzzy
      includeScore: true,
    });
  }, [skus]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSkusFiltered(
        fuse && search && search.trim() !== ""
          ? fuse.search(search.trim()).map((result) => result.item)
          : skus
      );
    }, 500);

    return () => clearTimeout(handler); // cleanup on every keystroke
  }, [search, skus, fuse]);

  const [selectionModel, setSelectionModel] = React.useState<number[]>([]);
  const handleBookInspection = useCallback(() => {
    const selectedIds = new Set(selectionModel);
    const selectedSkus = (skus ?? []).filter((sku) => selectedIds.has(sku.id));
    onSave(
      selectedSkus.map((model) => ({
        sku: model.sku,
        skuUniqueId: model.uniqueId,
        lotSize: 100,
        inspectionLevel: "GI",
      }))
    );
  }, [selectionModel, skus, onSave]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "uniqueId",
        headerName: "ID",
        width: 80,
        editable: false,
        renderCell: RefRenderCell,
      },
      {
        field: "sku",
        headerName: "SKU",
        width: 200,
        editable: true,
      },
      {
        field: "name",
        headerName: "Name",
        width: 200,
        editable: true,
      },
      {
        field: "description",
        headerName: "Description",
        width: 180,
        editable: true,
      },
    ],
    [RefRenderCell]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md" variant="outlined">
        <ModalClose />
        <DialogTitle>{t("Add SKUs to inspection")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex" }} gap={1}>
            <Input
              type="text"
              placeholder={t("Search...")}
              startDecorator={<SearchIcon />}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Box sx={{ flex: 1 }}></Box>
            <Button
              disabled={selectionModel.length === 0}
              variant="solid"
              color="primary"
              startDecorator={<FactCheckIcon />}
              onClick={handleBookInspection}
            >
              {t("Add to inspection")} ({selectionModel.length})
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 220px)",
            }}
          >
            {(skusFiltered ?? []).length > 0 && (
              <DataGrid
                checkboxSelection
                sx={{ backgroundColor: "var(--joy-palette-background-body)" }}
                rows={skusFiltered}
                columns={columns}
                editMode="cell"
                showColumnVerticalBorder
                showCellVerticalBorder
                rowHeight={36}
                disableVirtualization={true}
                disableColumnMenu
                disableColumnFilter
                disableRowSelectionOnClick
                slots={{
                  baseCheckbox: JoyCheckbox,
                }}
                onRowSelectionModelChange={(newSelection) => {
                  setSelectionModel(newSelection as number[]);
                }}
              />
            )}
          </Box>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}
